export const BLEED = 12;

const GOLD = '#d8c08a';
const GOLD_DIM = '#a8915f';
const VIOLET = '#b8a0ff';
const INK = '#0a0a0a';

// A concave 4-point spark / diamond centred at (cx,cy) with tip radius s.
function sparkPath(cx, cy, s) {
  const k = s * 0.18;
  return `M ${cx} ${cy - s}`
    + ` C ${cx} ${cy - k} ${cx + k} ${cy} ${cx + s} ${cy}`
    + ` C ${cx + k} ${cy} ${cx} ${cy + k} ${cx} ${cy + s}`
    + ` C ${cx} ${cy + k} ${cx - k} ${cy} ${cx - s} ${cy}`
    + ` C ${cx - k} ${cy} ${cx} ${cy - k} ${cx} ${cy - s} Z`;
}

// Tiny generic crown glyph (no trademark) drawn around centre (cx,cy), width s.
function crownPath(cx, cy, s) {
  const half = s / 2;
  const top = cy - s * 0.42;
  const dip = cy - s * 0.05;
  const base = cy + s * 0.34;
  return `M ${cx - half} ${base}`
    + ` L ${cx - half} ${top}`
    + ` L ${cx - half * 0.42} ${dip}`
    + ` L ${cx} ${top - s * 0.12}`
    + ` L ${cx + half * 0.42} ${dip}`
    + ` L ${cx + half} ${top}`
    + ` L ${cx + half} ${base} Z`;
}

// Vertical rail of rotated tiny gold text living inside the bleed margin on one
// edge. side = -1 (left, reads bottom-to-top) or +1 (right, reads top-to-bottom).
function SideRail({ cx, cy, label, side }) {
  const rot = side < 0 ? -90 : 90;
  return (
    <g transform={`translate(${cx} ${cy}) rotate(${rot})`}>
      <text x={0} y={0} fill={GOLD} fontSize={5.5} fontWeight="800" letterSpacing={2.4} textAnchor="middle" opacity={0.78}>
        {label}
      </text>
    </g>
  );
}

// "VIP all-access pass" frame. Black-card luxury look: a refined double border in
// champagne-gold with a faint iridescent (violet/gold) inner line to suggest holo
// foil, an "INVITATION ONLY" tag top-right, rotated gold side rails, a "PASS NO."
// barcode tag bottom-right and a tiny crowned seal bottom-left. All decoration
// hugs the border / corners / bleed so the centre content stays clear.
export default function VipFrame({ w, h, radius = 12, accent }) {
  const W = w + BLEED * 2;
  const H = h + BLEED * 2;
  const x = BLEED;
  const y = BLEED;
  const r = radius + 1;
  const gold = accent || GOLD;

  // Faux pass code + barcode, bottom-right, sitting in the bleed under the edge.
  const codeBars = [1.6, 0.8, 1.2, 0.6, 2, 0.8, 1, 1.4, 0.6, 1.8, 0.8, 1.2];
  const barH = 8;
  const barGap = 1.1;
  let barCursor = 0;
  const barW = codeBars.reduce((a, b) => a + b + barGap, 0);
  const barX0 = x + w - barW - 4;
  const barY = y + h + 1;

  return (
    <div style={{ position: 'absolute', top: -BLEED, left: -BLEED, width: W, height: H, pointerEvents: 'none' }}>
      <svg width={W} height={H} xmlns="http://www.w3.org/2000/svg">
        {/* Outer faint champagne glow */}
        <rect x={x - 2} y={y - 2} width={w + 4} height={h + 4} rx={r + 2} ry={r + 2} fill="none" stroke={GOLD_DIM} strokeWidth={0.8} opacity={0.35} />

        {/* Refined double border: outer crisp gold + inner thin gold line */}
        <rect x={x} y={y} width={w} height={h} rx={r} ry={r} fill="none" stroke={gold} strokeWidth={1.4} opacity={0.92} />
        <rect x={x + 3} y={y + 3} width={w - 6} height={h - 6} rx={Math.max(2, r - 3)} ry={Math.max(2, r - 3)} fill="none" stroke={gold} strokeWidth={0.6} opacity={0.5} />

        {/* Iridescent holo inner line: alternate violet + gold dashed stops */}
        <rect x={x + 5} y={y + 5} width={w - 10} height={h - 10} rx={Math.max(2, r - 5)} ry={Math.max(2, r - 5)} fill="none" stroke={VIOLET} strokeWidth={0.7} opacity={0.4} strokeDasharray="6 7" />
        <rect x={x + 5} y={y + 5} width={w - 10} height={h - 10} rx={Math.max(2, r - 5)} ry={Math.max(2, r - 5)} fill="none" stroke={gold} strokeWidth={0.7} opacity={0.4} strokeDasharray="6 7" strokeDashoffset={6} />

        {/* TOP-RIGHT: INVITATION ONLY tag with divider + spark */}
        <g>
          <text x={x + w - 16} y={y - 4} fill={gold} fontSize={6} fontWeight="800" letterSpacing={1.6} textAnchor="end" opacity={0.9}>
            INVITATION ONLY
          </text>
          <line x1={x + w - 92} y1={y - 1.5} x2={x + w - 12} y2={y - 1.5} stroke={GOLD_DIM} strokeWidth={0.7} opacity={0.55} />
          <path d={sparkPath(x + w - 6, y - 6, 3.2)} fill={gold} opacity={0.9} />
        </g>

        {/* LEFT + RIGHT edge rails (rotated tiny gold text, inside the bleed) */}
        <SideRail cx={x - 6} cy={y + h / 2} label="ACCESS LEVEL" side={-1} />
        <SideRail cx={x + w + 6} cy={y + h / 2} label="NIGHT IS YOURS" side={1} />

        {/* BOTTOM-RIGHT: PASS NO. tag + faux code + tiny barcode */}
        <g>
          <text x={x + w - 4} y={y + h + 8} fill={gold} fontSize={5} fontWeight="800" letterSpacing={1} textAnchor="end" opacity={0.72}>
            PASS NO. V23 · 0725 · X
          </text>
          {codeBars.map((bw, i) => {
            const bx = barX0 + barCursor;
            barCursor += bw + barGap;
            return (
              <rect key={`b${i}`} x={bx} y={barY} width={bw} height={barH} fill={gold} opacity={0.7} />
            );
          })}
        </g>

        {/* BOTTOM-LEFT: small VIP seal — circle badge with a crown glyph */}
        <g>
          <circle cx={x + 8} cy={y + h + 2} r={8.5} fill={INK} opacity={0.9} />
          <circle cx={x + 8} cy={y + h + 2} r={8.5} fill="none" stroke={gold} strokeWidth={1} opacity={0.9} />
          <circle cx={x + 8} cy={y + h + 2} r={6.2} fill="none" stroke={VIOLET} strokeWidth={0.5} opacity={0.5} />
          <path d={crownPath(x + 8, y + h + 1.5, 8)} fill="none" stroke={gold} strokeWidth={0.9} opacity={0.95} />
        </g>
      </svg>
    </div>
  );
}
